import React, { useEffect } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Progress,
  Space,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Close } from "@payping/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { ValidSheba } from "@payping/utils/validation";
import styles from "@/assets/scss/profile/Profile.module.scss";
import { buildUrl } from "@payping/utilities-shared/url";
import { DashboardRoutes } from "@/router/constant";
import { DashboardChecksTypeEnum } from "@payping/api";
import { useAsyncRetry } from "react-use";
import { getUserBank, getUserChecks, updateBank } from "@payping/redux/actions";
import EditActionButtons from "@/components/mobile/settings/EditActionButtons";
import { URLS } from "@payping/utilities-shared/consts";

const MobileEditShaba: React.FC = () => {
  const currentURL = useLocation().pathname;
  const isRegisterURL = currentURL === DashboardRoutes.register.bank.url;
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const [form] = Form.useForm();
  const { sheba }: any = useAppSelector((state) => state.user.bankInfo);
  const navigate = useNavigate();
  const bankStatus = useAppSelector((state) => state.user.checks?.bankStatus);
  const isBankDisabled = bankStatus === DashboardChecksTypeEnum.Confirmed;
  const { settings } = DashboardRoutes;

  const { loading } = useAsyncRetry(() => dispatch(getUserBank()), [dispatch]);

  useMessagedAsync(() => dispatch(getUserChecks()), [isBankDisabled], { useProgress: true });

  useEffect(() => {
    if (sheba) {
      form.setFieldsValue({ shaba: sheba.split("IR")[1] });
    }
  }, [dispatch, form, sheba]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(async (values) => {
    const shaba = `IR${values.shaba}`;
    await dispatch(updateBank({ shaba }));
    if (!isRegisterURL) {
      navigate(DashboardRoutes.settings.bank.root.url);
    } else navigate(-1);
  }, []);

  return (
    <>
      <Drawer
        open={true}
        placement={"bottom"}
        height={"100%"}
        title={isRegisterURL ? t("settings.business.paymentGatewayRequestForm") : t("settings.bank.editShebaNumber")}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(settings.bank.root.url);
            }}
          />
        }
      >
        <Space direction={"vertical"} size={"middle"} className={"tw-mb-4 tw-w-full"}>
          <Typography.Title level={4}>{t("profile.main.bank")}</Typography.Title>
          {isRegisterURL ? (
            <Progress steps={3} percent={90} format={(percent) => `${(percent || 30) / 30} از 3`} />
          ) : null}
        </Space>

        <Space direction={"vertical"} className={"tw-w-full"}>
          <Form form={form} onFinish={onSubmit} layout={"vertical"}>
            <Form.Item label={t("profile.shaba")}>
              <Form.Item
                noStyle
                name="shaba"
                rules={[
                  { required: true },
                  {
                    validator(_, value) {
                      if (value && !ValidSheba(`IR${value}`)) {
                        return Promise.reject(new Error(t("shabaValidation")));
                      }
                      const bankType = value.substr(2, 3);
                      // TODO: this is temporary and should be removed soon,
                      // 052 is type of old ghavamin IBAN numbers
                      // 063 is type of old ansar IBAN numbers
                      if (value && bankType === "052") {
                        return Promise.reject(new Error(t("shabaGhavaminValidation")));
                      }
                      if (value && bankType === "063") {
                        return Promise.reject(new Error(t("shabaAnsarValidation")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className={styles.inputLtr}
                  suffix={"IR"}
                  skeleton={loading || loadingSubmit}
                  onlyNumbers
                  canStartWithZero
                />
              </Form.Item>
            </Form.Item>
          </Form>
          <Button
            target="_blank"
            rel="norefferer"
            href={buildUrl(URLS.BLOG_URL, "sheba").toString()}
            loading={loading}
            type={"link"}
          >
            {t("profile.ShabaGiudeline")}
          </Button>
        </Space>
      </Drawer>
      <EditActionButtons loading={loading || loadingSubmit} onAccept={form.submit} />
    </>
  );
};

export default MobileEditShaba;
